// What the Systems panel on construction tracking shows, from three reads
// (owner, 2026-10-26: "a system panel on the construction tracking, that
// shows all available systems, per area, per level, each in its own tab,
// based on all levels in levels/areas and system, and ... an option to add
// missing systems so they can be synced to levels, areas, systems and then
// can be added to the schedule"):
//   pages    GET /areas/levels         the level pages, in the tab's order
//   areas    GET /areas/systems        each zone's systems + suggestions
//   byLevel  GET /areas/systems/by-level  tagged elements per level/area/system
//                                       (null while it is still counting)
//
// A zone's systems belong to the ZONE, not to one level (BA03's A-F serve
// Level 1, Level 2 and Roof), so adding one here adds it on every level
// that shows the area - the same write the Levels, Areas & Systems page
// makes. Per level, an area shows:
//   onArea    the systems it already has, each with its tagged count HERE
//   missing   systems its tagged elements on THIS level belong to that the
//             area does not have yet - the strong "add me"
//   elsewhere systems other areas of this level have and this one neither
//             has nor shows elements for - a quiet quick-add, for an area
//             whose elements the tagging gave to a smaller zone (BA03 B's
//             equipment sits in the K-Line / L-Line strips)
// Pure, for the test.

const lc = (s) => String(s || "").toLowerCase();
const byCount = (a, b) => (b.count - a.count) || String(a.name).localeCompare(String(b.name));

export function levelTabs({ pages, areas, byLevel }) {
  const areaById = new Map((areas || []).map((a) => [String(a.areaId), a]));
  const counted = byLevel && byLevel.pages ? byLevel.pages : null;
  return (pages || []).map((p) => {
    const pid = String(p._id);
    const perArea = (counted && counted[pid]) || {};
    const rows = [];
    for (const id of p.areaIds || []) {
      const a = areaById.get(String(id));
      if (!a) continue; // not on the current map, or not shown to this reader
      const here = perArea[String(id)] || {};
      const have = new Set((a.systems || []).map((s) => lc(s.name)));
      const sugg = new Map((a.suggestions || []).map((s) => [lc(s.name), s]));
      const onArea = (a.systems || []).map((s) => ({
        name: s.name,
        count: counted ? Number(here[s.name] || 0) : null,
        source: s.source || "",
      }));
      let missing;
      if (counted) {
        missing = Object.entries(here)
          .filter(([n, c]) => c > 0 && !have.has(lc(n)))
          .map(([n, c]) => ({ name: n, count: c, suggested: sugg.has(lc(n)) }));
      } else {
        // Before the per-level count arrives: the area-wide suggestions.
        missing = (a.suggestions || [])
          .filter((s) => s.state === "new")
          .map((s) => ({ name: s.name, count: s.elements, suggested: true, areaWide: true }));
      }
      missing.sort(byCount);
      const total = counted ? Object.values(here).reduce((t, c) => t + (Number(c) || 0), 0) : null;
      rows.push({
        areaId: String(id), name: a.name, code: a.code || "", color: a.color || "", status: a.status,
        onArea, missing, total, unmapped: a.unmapped || [],
      });
    }
    // Every system any area of this level has or shows, for the quiet chips.
    const levelSystems = new Map();
    for (const r of rows) {
      for (const s of r.onArea) if (!levelSystems.has(lc(s.name))) levelSystems.set(lc(s.name), s.name);
      for (const s of r.missing) if (!levelSystems.has(lc(s.name))) levelSystems.set(lc(s.name), s.name);
    }
    for (const r of rows) {
      const shown = new Set([...r.onArea, ...r.missing].map((s) => lc(s.name)));
      r.elsewhere = [...levelSystems.entries()].filter(([k]) => !shown.has(k)).map(([, n]) => n).sort();
    }
    // The level's own totals, system by system, across its areas.
    const totals = new Map();
    for (const r of rows) {
      for (const s of [...r.onArea, ...r.missing]) {
        if (s.count == null) continue;
        totals.set(s.name, (totals.get(s.name) || 0) + s.count);
      }
    }
    return {
      pageId: pid,
      name: p.name,
      rows,
      missingCount: rows.reduce((t, r) => t + r.missing.length, 0),
      systems: [...totals.entries()].map(([name, count]) => ({ name, count })).sort(byCount),
      counted: !!counted,
    };
  });
}

// How one add is sent: a name the tagged elements suggest is ACCEPTED (it
// keeps "accepted from N tagged elements" on the area); any other name is
// added by hand. -> { accept: [names], add: [names] }
export function addPlan(area, names) {
  const sugg = new Set(((area && area.suggestions) || []).filter((s) => s.state !== "accepted").map((s) => lc(s.name)));
  const have = new Set(((area && area.systems) || []).map((s) => lc(s.name)));
  const out = { accept: [], add: [] };
  const seen = new Set();
  for (const raw of names || []) {
    const n = String(raw || "").replace(/\s+/g, " ").trim();
    if (!n || have.has(lc(n)) || seen.has(lc(n))) continue;
    seen.add(lc(n));
    (sugg.has(lc(n)) ? out.accept : out.add).push(n);
  }
  return out;
}
